import * as React from 'react';
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import ReactTooltip from 'react-tooltip';
import Select, { MultiValue } from 'react-select';
import styles from './pref_webhooks.module.css';
import { getLogger } from '../../services/logger.service';
import memberStore from '../../stores/member.store';
import { IWebhookIntegration, WebhookEventType } from '../../models/account.model';
import Popup, { PopupInfo } from '../common/popup.component';
import Switch from '../common/switch.component';
import Copy from '../common/copy.component';

import trash from '../../img/preferences/trash.svg';
import pen from '../../img/preferences/pen.svg';
import send from '../../img/preferences/send.svg';

const log = getLogger('PrefWebhooks');

type HeaderRow = {key: string; value: string};
type AppOption = {value: string; label: string};

const recordToRows = (headers?: Record<string, string>): HeaderRow[] => {
  const rows = Object.entries(headers ?? {}).map(([key, value]) => ({key, value}));
  return rows.length ? rows : [{key: '', value: ''}];
};

const rowsToRecord = (rows: HeaderRow[]): Record<string, string> =>
  rows.reduce((acc, h) => {
    if (h.key.trim()) acc[h.key.trim()] = h.value;
    return acc;
  }, {} as Record<string, string>);

const showSecret = (secret?: string) => {
  if (!secret) return;
  Popup.create({
    title: 'Signing secret',
    msg: (
      <div className={styles.secretPopup}>
        <div className={styles.secretNote}>This signing secret is shown only once. Verifying the X-Shipbook-Signature header is optional but recommended — if you plan to verify, copy it now; otherwise you can ignore it.</div>
        <textarea readOnly className={styles.secretText} value={secret} />
        <Copy text={secret} />
      </div>
    ),
    ok: 'Done'
  });
};

const PrefWebhooks = observer(() => {
  const account = memberStore.account;
  const webhooks = account?.webhookIntegration ?? [];

  const [url, setUrl] = useState('');
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([{key: '', value: ''}]);
  const [apps, setApps] = useState<string[]>([]);

  useEffect(() => { ReactTooltip.rebuild(); }, [webhooks.length]);

  const add = async () => {
    if (!url) return;
    if (!/^https:\/\//i.test(url)) {
      Popup.create({title: 'Invalid URL', msg: 'The webhook URL must start with https://', ok: 'OK'});
      return;
    }
    const headers = rowsToRecord(headerRows);
    const secret = await memberStore.addWebhookIntegration({
      url,
      headers: Object.keys(headers).length ? headers : undefined,
      apps: apps.length ? apps : undefined
    });
    setUrl('');
    setHeaderRows([{key: '', value: ''}]);
    setApps([]);
    showSecret(secret);
  };

  return (
    <div className={styles.component}>
      <div className={styles.title}>Webhooks</div>
      <div className={styles.subTitle}>
        Send custom and automatic alerts to your own API. Shipbook POSTs a signed JSON payload to each
        endpoint below when an alert fires, so you can route alerts into your own systems.
      </div>

      <div className={styles.list}>
        {webhooks.map((integration) => (
          <WebhookItem key={integration._id} integration={integration} />
        ))}
      </div>

      <div className={styles.underline} />

      <div className={styles.addPanel}>
        <div className={styles.addRow}>
          <input type="url" className={styles.input} placeholder="https://your-api.example.com/shipbook/alerts"
            value={url} onChange={(e) => setUrl(e.target.value)} />
          <button className={styles.addButton} onClick={add}>add</button>
        </div>
        <HeadersEditor rows={headerRows} setRows={setHeaderRows} />
        <div className={styles.appsRow}>
          <span className={styles.appsLabel}>Apps:</span>
          <AppSelect selected={apps} setSelected={setApps} />
        </div>
      </div>
    </div>
  );
});

export default PrefWebhooks;

const HeadersEditor = ({rows, setRows}: {rows: HeaderRow[]; setRows: (rows: HeaderRow[]) => void}) => {
  const setHeader = (i: number, field: 'key' | 'value', value: string) =>
    setRows(rows.map((h, idx) => idx === i ? {...h, [field]: value} : h));
  const addRow = () => setRows([...rows, {key: '', value: ''}]);
  const removeRow = (i: number) =>
    setRows(rows.length === 1 ? [{key: '', value: ''}] : rows.filter((_, idx) => idx !== i));

  return (
    <div className={styles.headers}>
      <div className={styles.headersLabel}>Headers (optional)</div>
      {rows.map((h, i) => (
        <div className={styles.headerRow} key={i}>
          <input type="text" className={styles.input} placeholder="Header name"
            value={h.key} onChange={(e) => setHeader(i, 'key', e.target.value)} />
          <input type="text" className={styles.input} placeholder="Value"
            value={h.value} onChange={(e) => setHeader(i, 'value', e.target.value)} />
          <button className={styles.headerRemove} onClick={() => removeRow(i)} aria-label="remove header">✕</button>
        </div>
      ))}
      <button className={styles.addHeaderButton} onClick={addRow}>+ add header</button>
    </div>
  );
};

const AppSelect = observer(({selected, setSelected}: {selected: string[]; setSelected: (apps: string[]) => void}) => {
  const apps = memberStore.account?.apps ?? [];
  const options: AppOption[] = apps.map((app) => ({value: app._id, label: app.name}));
  const value = options.filter((o) => selected.includes(o.value));
  return (
    <Select
      isMulti
      className={styles.appSelect}
      classNamePrefix="appSelect"
      placeholder="All apps"
      options={options}
      value={value}
      onChange={(sel: MultiValue<AppOption>) => setSelected(sel.map((o) => o.value))}
      menuPortalTarget={document.body}
      styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
      theme={(theme) => ({...theme, colors: {...theme.colors, primary: 'var(--marine)', primary25: 'var(--ice-blue-dark)'}})}
    />
  );
});

const WebhookItem = observer(({integration}: {integration: IWebhookIntegration}) => {
  const apps = memberStore.account!.apps;
  const scopedApps = integration.apps ?? [];

  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(integration.url);
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>(recordToRows(integration.headers));
  const [editApps, setEditApps] = useState<string[]>(scopedApps);

  const startEdit = () => {
    setUrl(integration.url);
    setHeaderRows(recordToRows(integration.headers));
    setEditApps(integration.apps ?? []);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!/^https:\/\//i.test(url)) {
      Popup.create({title: 'Invalid URL', msg: 'The webhook URL must start with https://', ok: 'OK'});
      return;
    }
    const headers = rowsToRecord(headerRows);
    await memberStore.updateWebhookIntegration(integration._id, {
      url,
      headers: Object.keys(headers).length ? headers : undefined,
      apps: editApps.length ? editApps : undefined,
      enabled: integration.enabled
    });
    setEditing(false);
  };

  const regenerate = () => {
    Popup.create({
      title: 'Regenerate signing secret',
      msg: 'This invalidates the current secret. Your endpoint must be updated with the new one before requests will verify. Continue?',
      ok: 'Replace',
      okCallback: async () => {
        const secret = await memberStore.regenerateWebhookSecret(integration._id);
        showSecret(secret);
        return false; // keep the just-opened secret popup; don't let this confirm close it
      },
      cancel: 'Cancel'
    });
  };

  const toggleEnabled = (one: boolean) =>
    memberStore.updateWebhookIntegration(integration._id, {
      url: integration.url,
      headers: integration.headers,
      apps: integration.apps,
      enabled: !one
    });

  const onTrash = () => {
    const info: PopupInfo = {
      title: 'Delete',
      msg: `Are you sure that you want to delete the webhook ${integration.url} from the account?`,
      ok: 'Delete',
      okCallback: async () => {
        await memberStore.removeWebhookIntegration(integration._id);
      },
      cancel: 'Cancel'
    };
    Popup.create(info);
  };

  const test = async (type: WebhookEventType) => {
    try {
      await memberStore.testWebhookIntegration(integration._id, type);
      Popup.create({title: 'Test sent', msg: `A ${type} test event was delivered successfully.`, ok: 'OK'});
    } catch (err) {
      log.e('webhook test failed', err);
      Popup.create({title: 'Test failed', msg: 'The test event could not be delivered. Check the URL and that your endpoint responds with a 2xx status.', ok: 'OK'});
    }
  };

  const chooseTest = () => {
    Popup.create({
      title: 'Send test event',
      msg: (
        <div className={styles.testChoices}>
          <button className={styles.choiceButton} onClick={() => { Popup.close(); test(WebhookEventType.Custom); }}>Custom alert</button>
          <button className={styles.choiceButton} onClick={() => { Popup.close(); test(WebhookEventType.Automatic); }}>Automatic alert</button>
        </div>
      ),
      ok: ''
    });
  };

  if (editing) {
    return (
      <div className={styles.webhookItem}>
        <div className={styles.editForm}>
          <div className={styles.addRow}>
            <input type="url" className={styles.input} placeholder="https://your-api.example.com/shipbook/alerts"
              value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <HeadersEditor rows={headerRows} setRows={setHeaderRows} />
          <div className={styles.appsRow}>
            <span className={styles.appsLabel}>Apps:</span>
            <AppSelect selected={editApps} setSelected={setEditApps} />
          </div>
          <button className={styles.regenButton} onClick={regenerate}>Regenerate signing secret</button>
          <div className={styles.editButtons}>
            <button className={styles.saveButton} onClick={saveEdit}>save</button>
            <button className={styles.cancelButton} onClick={() => setEditing(false)}>cancel</button>
          </div>
        </div>
      </div>
    );
  }

  const appNames = scopedApps
    .map((id) => apps.find((a) => a._id === id)?.name)
    .filter(Boolean)
    .join(', ');

  return (
    <div className={styles.webhookItem}>
      <div className={styles.itemRow}>
        <div className={styles.url}>{integration.url}</div>
        <div className={styles.apps}>
          <span className={styles.appsLabel}>Apps:</span>
          {scopedApps.length
            ? <span className={styles.appsNames}>{appNames}</span>
            : <span className={styles.appsHint}>all apps</span>}
        </div>
        <Switch oneMode={!integration.enabled} onClick={toggleEnabled}
          dataTip1="Disable — pause alert delivery" dataTip2="Enable — deliver alerts" />
        <img alt="send test event" data-tip="Send test event" className={styles.send} src={send} onClick={chooseTest} />
        <img alt="edit" data-tip="Edit" className={styles.pen} src={pen} onClick={startEdit} />
        <img alt="delete" data-tip="Delete" className={styles.trash} src={trash} onClick={onTrash} />
      </div>
    </div>
  );
});
